import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useWorkosCallback } from '@fluxter/shared';
import { Loader2 } from 'lucide-react';
import { tokenStore } from '@/lib/api';
import { isAccessGateBlocked } from '@/lib/auth-errors';
import { consumeWebCallback } from '@/lib/sign-in';
import { consumePostLoginReturnUrl } from '@/lib/post-login-redirect';
import { AuthHeader, AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';

export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const triggered = useRef(false);

  const callback = useWorkosCallback(async (token) => {
    await tokenStore.set(token);
  });

  useEffect(() => {
    if (triggered.current) return;
    triggered.current = true;

    const params = consumeWebCallback();
    if (!params) {
      void navigate({ to: '/login' });
      return;
    }

    callback
      .mutateAsync({
        code: params.code,
        code_verifier: params.codeVerifier,
        device_name: 'web',
      })
      .then(() => {
        // Honour a stashed post-login return URL (typically the
        // invitation accept page that bounced through sign-in).
        const returnUrl = consumePostLoginReturnUrl();
        if (returnUrl) {
          window.location.assign(returnUrl);
          return;
        }
        return navigate({ to: '/' });
      })
      .catch((e: unknown) => {
        // Access-gate rejection (Slice-1+2 WorkOS Organization
        // membership, or the legacy beta-allowlist code path) has
        // its own polished page; bounce there instead of showing
        // the generic error block. Renderer never received a token
        // (API rejected before issuing one), so no cleanup needed.
        if (isAccessGateBlocked(e)) {
          void navigate({ to: '/access-denied', replace: true });
          return;
        }
        setError(e instanceof Error ? e.message : 'Sign-in failed.');
      });
  }, [callback, navigate]);

  return (
    <AuthShell
      width="sm"
      header={
        <AuthHeader
          title={error ? 'Sign-in failed' : 'Finishing sign-in…'}
          subtitle={error ? undefined : 'One moment while we finish the handshake.'}
        />
      }
    >
      {error ? (
        <div className="space-y-4 text-center">
          <p className="text-destructive text-[12.5px]" role="alert">
            {error}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => void navigate({ to: '/login' })}
            className="w-full"
          >
            Back to sign in
          </Button>
        </div>
      ) : (
        <p className="text-foreground-muted flex items-center justify-center gap-2 text-[12.5px]">
          <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          Verifying credentials
        </p>
      )}
    </AuthShell>
  );
};
